import axios from 'axios'
import {
    CommonPortionResponse,
    CommonStatusHistoryResponse,
    HierarchicalPortionTreeResponse, NextPortionCodesParams
} from '@/api/services/common/portion/common-portion.dto'
import {
    CommonParamsWithQuery,
    CommonPortionFamilyTreeRequest,
    CommonSearchResponse
} from '@/api/resources/common/shared/models/common.models'

class CommonPortionService {
    /**
     * returns detail information of the portion node
     *
     * @param laboratoryId
     * @param caseId
     * @param sampleId
     * @param portionId
     */
    async getPortion(
        laboratoryId: number,
        caseId: number,
        sampleId: number,
        portionId: number
    ): Promise<CommonPortionResponse> {
        const response =
            await axios.get<CommonPortionResponse>(
                `/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}`)
        return response.data
    }

    /**
     * returns hierarchical portion tree
     *
     * @param laboratoryId
     * @param caseId
     * @param sampleId
     * @param portionId
     */
    async getHierarchicalPortionIndexTree(
        laboratoryId: number,
        caseId: number,
        sampleId: number,
        portionId: number
    ): Promise<HierarchicalPortionTreeResponse> {
        const response =
            await axios.get<HierarchicalPortionTreeResponse>(
                `/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/hierarchicalPortionIndexTree`)
        return response.data
    }

    /**
     * returns detail information of the sample node
     *
     * @param laboratoryId
     * @param caseId
     * @param sampleId
     * @param portionId
     */
    async getTracking(
        laboratoryId: number,
        caseId: number,
        sampleId: number,
        portionId: number
    ): Promise<CommonStatusHistoryResponse[]> {
        const response = await axios.get<CommonStatusHistoryResponse[]>(
            `/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/tracking`)
        return response.data
    }

    /**
     * returns detail information of all the portions of a given case
     *
     * @param laboratoryId
     * @param caseId
     */
    async getCasePortions(
        laboratoryId: number,
        caseId: number
    ): Promise<CommonPortionResponse[]> {
        const response =
            await axios.get<CommonPortionResponse[]>(
                `/common/laboratories/${laboratoryId}/cases/${caseId}/samples/portions`)
        return response.data
    }

    /**
     * returns detail information of the portion node
     *
     * @param laboratoryId
     * @param portionLabel
     */
    async findByLabel(
        laboratoryId: number,
        portionLabel: string
    ): Promise<CommonPortionResponse[]> {
        const response = await axios.get<CommonPortionResponse[]>(
            `/common/laboratories/${laboratoryId}/cases/samples/portions/${portionLabel}/findByLabel`)
        return response.data
    }

    /**
     * Returns the following identifiers for the portion from a temporary sample (not saved yet)
     *
     * @param laboratoryId
     * @param params
     */
    async getNextPortionCodes(
        laboratoryId: number,
        params: NextPortionCodesParams
    ): Promise<string[]> {
        const response = await axios.get<string[]>(
            `/common/laboratories/${laboratoryId}/cases/samples/portions/getNextPortionCodes`,
            { params })
        return response.data
    }

    /**
     * search portions by label
     *
     * @param params
     */
    async search(
        params: CommonParamsWithQuery
    ): Promise<CommonSearchResponse[]> {
        const response = await axios.get<CommonSearchResponse[]>(
            '/common/portions/search',
            { params })
        return response.data
    }

    async markPortionsAsDepleted(
        laboratoryId: number,
        portionIdentifiers: CommonPortionFamilyTreeRequest[],
        cancelSubportionsPendingCut: boolean
    ): Promise<CommonSearchResponse[]> {
        const requestConfig = {
            params: { cancelSubportionsPendingCut }
        }
        const response = await axios.put(
            `/common/laboratories/${laboratoryId}/cases/samples/portions/markAsDepleted`,
            { entityIdentifiers: portionIdentifiers },
            requestConfig
        )
        return response.data
    }
    async markPortionsAsNonDepleted(
        laboratoryId: number,
        portionIdentifiers: CommonPortionFamilyTreeRequest[]
    ): Promise<CommonSearchResponse[]> {
        const response = await axios.put(
            `/common/laboratories/${laboratoryId}/cases/samples/portions/markAsNonDepleted`,
            { entityIdentifiers: portionIdentifiers }
        )
        return response.data
    }
}

export const commonPortionService = new CommonPortionService()
